import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { OrganizationManageService } from '../../../core/services/organization-manage.service';
import { DialogService } from '../../../core/services/dialog.service';
import { OrganizationFormComponent } from '../organization-form/organization-form.component';
import { firstValueFrom } from 'rxjs';
import { Organization } from '../../../shared/interfaces/organization.interface';
import { FEATURE_FLAGS } from '../../../shared/constants/feature-flags';

@Component({
  selector: 'app-organization-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatSnackBarModule,
    MatDialogModule
  ],
  templateUrl: './organization-list.component.html',
  styleUrl: './organization-list.component.scss'
})
export class OrganizationListComponent {
  protected readonly organizationService = inject(OrganizationManageService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialogService = inject(DialogService);
  private readonly dialog = inject(MatDialog);
  
  public displayedColumns: string[] = ['name', 'features', 'actions'];

  public formatFeatureName(feature: string): string {
    const flag = FEATURE_FLAGS.find(f => f.id === feature);
    return flag ? flag.name : feature;
  }

  public async openForm(organization?: Organization): Promise<void> {
    const dialogRef = this.dialog.open(OrganizationFormComponent, {
      width: '500px',
      data: { organization }
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result) {
      try {
        if (organization) {
          await this.organizationService.updateOrganization(organization.id, result);
          this.snackBar.open('組織を更新しました', '閉じる', { duration: 3000 });
        } else {
          await this.organizationService.createOrganization(result);
          this.snackBar.open('新規組織を作成しました', '閉じる', { duration: 3000 });
        }
        this.organizationService.reloadOrganizations(); 
      } catch (error) {
        console.error('Failed to save organization', error);
        this.snackBar.open('組織の保存に失敗しました', '閉じる', { duration: 3000 });
      }
    }
  }

  public async deleteOrganization(id: string, name: string): Promise<void> {
    const confirmed = await firstValueFrom(this.dialogService.confirm({
      title: '確認',
      message: `組織「${name}」を削除してもよろしいですか？`,
      confirmText: '削除',
      cancelText: 'キャンセル',
      color: 'warn'
    }));

    if (confirmed) {
      try {
        await this.organizationService.deleteOrganization(id);
        this.snackBar.open('組織を削除しました', '閉じる', { duration: 3000 });
        this.organizationService.reloadOrganizations();
      } catch (error: unknown) {
        console.error('Failed to delete organization', error);
        const message = error instanceof Error ? error.message : '不明なエラー';
        this.snackBar.open(`組織の削除に失敗しました: ${message}`, '閉じる', { duration: 5000 });
      }
    }
  }
}
